/* eslint-disable no-param-reassign */
import { create } from 'zustand';
import { StorageValue, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { SimInventorySegmentSelectionState } from './SimInventoryStore';
import type { SimInventorySelectionState } from './types';

const STORE_NAME = 'simInventorySelection' as const;
const STORE_VERSION = 4 as const;

const defaultSegmentState: SimInventorySegmentSelectionState = { selected: {} };

const useSimInventorySelectionStore = create<SimInventorySelectionState>()(
  persist(
    immer(
      devtools(
        (set) => ({
          segmentStates: structuredClone({
            all: { ...defaultSegmentState },
            active: { ...defaultSegmentState },
            deactivated: { ...defaultSegmentState },
            preflight: { ...defaultSegmentState },
          }),

          /* Selections */
          resetSelection: (segmentName) =>
            set(
              (state) => {
                state.segmentStates[segmentName].selected = { ...defaultSegmentState.selected };
              },
              false,
              { type: 'simInventorySelection/resetSelection', segmentName }
            ),
          setSelectedSims: (segmentName, sims, allState) =>
            set(
              (state) => {
                const newSims = new Map(sims);
                state.segmentStates[segmentName].selected = {
                  sims: newSims,
                  all: allState,
                };
              },
              false,
              { type: 'simInventorySelection/setSelectedSims', segmentName, sims }
            ),
          addSelectedSim: (segmentName, simIds) =>
            set(
              (state) => {
                state.segmentStates[segmentName].selected = {
                  sims: new Map(state.segmentStates[segmentName].selected.sims).set(
                    simIds.hologramSimId,
                    simIds.claimed_device_id
                  ),
                  all: 'indeterminate',
                };
              },
              false,
              { type: 'simInventorySelection/addSelectedSim', segmentName, simIds }
            ),
          removeSelectedSim: (segmentName, simIds) =>
            set(
              (state) => {
                const sims = new Map(state.segmentStates[segmentName].selected.sims);
                sims.delete(simIds.hologramSimId);

                state.segmentStates[segmentName].selected = {
                  sims,
                  all: sims.size > 0 ? 'indeterminate' : undefined,
                };
              },
              false,
              { type: 'simInventorySelection/removeSelectedSim', segmentName, simIds }
            ),
        }),
        {
          name: STORE_NAME,
          enabled: (process.env.VITE_ENV_NAME ?? 'development') === 'development',
        }
      )
    ),
    {
      name: STORE_NAME,
      version: STORE_VERSION,
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) {
            return null;
          }

          const { state } = JSON.parse(str, (key, value) => {
            if (key === 'sims') {
              return new Map(value);
            }
            return value;
          });

          return {
            state,
          };
        },
        setItem: (name, newValue: StorageValue<SimInventorySelectionState>) => {
          const str = JSON.stringify(
            {
              state: newValue.state,
            },
            (key, value) => {
              if (key === 'sims') {
                return Array.from(value);
              }
              return value;
            }
          );
          sessionStorage.setItem(name, str);
        },
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    }
  )
);

export default useSimInventorySelectionStore;
